// Relative "time ago" string that matches the v1 admin UI. v1 formats the same
// `last_motion` / `camera_start_time` epoch with Django's `django.utils.timesince`,
// so this is a direct port of that function (calendar-aware years/months,
// two-unit depth, e.g. "1 hour, 55 minutes ago"). Using a fixed average month
// length here instead diverges from v1 near month boundaries (e.g. 182 days is
// 6 calendar months but only 5.98 average months).
//
// Django computes with naive datetimes in the server timezone (UTC), so we use
// UTC calendar components to get the same month/year boundaries regardless of
// the viewer's timezone.

// Django's MONTHS uses a flat 28 for February (no leap-year handling); mirror it.
const MONTH_DAYS = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

// Sub-month chunks, in seconds, in Django's order.
const CHUNKS: [number, string][] = [
  [604800, "week"],
  [86400, "day"],
  [3600, "hour"],
  [60, "minute"],
];

const DEPTH = 2;

const label = (value: number, name: string) =>
  `${value} ${name}${value === 1 ? "" : "s"}`;

export const timeSinceConvertor = (epochNum: number) => {
  const d = new Date(Math.floor(epochNum) * 1000);
  const now = new Date();

  const since = Math.floor((now.getTime() - d.getTime()) / 1000);
  if (since <= 0) return "0 minutes ago";

  // Whole calendar years + months between the two dates.
  let totalMonths =
    (now.getUTCFullYear() - d.getUTCFullYear()) * 12 +
    (now.getUTCMonth() - d.getUTCMonth());
  const dMsOfDay = ((d.getTime() % 86400000) + 86400000) % 86400000;
  const nowMsOfDay = ((now.getTime() % 86400000) + 86400000) % 86400000;
  if (
    d.getUTCDate() > now.getUTCDate() ||
    (d.getUTCDate() === now.getUTCDate() && dMsOfDay > nowMsOfDay)
  ) {
    totalMonths -= 1;
  }
  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;

  // Pivot: shift d forward by the whole years+months, then measure the rest.
  let pivotMs: number;
  if (years || months) {
    let pivotYear = d.getUTCFullYear() + years;
    let pivotMonth = d.getUTCMonth() + months; // 0-indexed
    if (pivotMonth > 11) {
      pivotMonth -= 12;
      pivotYear += 1;
    }
    const pivotDay = Math.min(MONTH_DAYS[pivotMonth], d.getUTCDate());
    pivotMs = Date.UTC(
      pivotYear,
      pivotMonth,
      pivotDay,
      d.getUTCHours(),
      d.getUTCMinutes(),
      d.getUTCSeconds()
    );
  } else {
    pivotMs = d.getTime();
  }

  let remaining = Math.floor((now.getTime() - pivotMs) / 1000);
  const partials: [number, string][] = [
    [years, "year"],
    [months, "month"],
  ];
  for (const [chunk, name] of CHUNKS) {
    const count = Math.floor(remaining / chunk);
    partials.push([count, name]);
    remaining -= chunk * count;
  }

  // Start at the first non-zero unit, then take up to DEPTH adjacent units,
  // stopping at the first zero (matches Django).
  const parts: string[] = [];
  let started = false;
  for (const [value, name] of partials) {
    if (!started) {
      if (value === 0) continue;
      started = true;
    } else if (value === 0) {
      break;
    }
    parts.push(label(value, name));
    if (parts.length >= DEPTH) break;
  }

  if (parts.length === 0) return "0 minutes ago";
  return `${parts.join(", ")} ago`;
};
